import { QueryClient, HydrationBoundary, dehydrate, useQueryClient, type DehydratedState } from '@tanstack/react-query';
import { createElement, type ComponentType } from 'react';
import { api } from '@/lib/api/client';
import type { Source } from '@/features/classification-catalog/api';
import { eviFor, lurDayQuery, type ClassificationPref, type DayListItem, type DayResp, type ThemeType } from './api';

export interface ReviewBootstrap {
  classification: ClassificationPref;
  day: DayResp | null;
  day_error: unknown;
  days: { days: DayListItem[] };
  sources: Record<'theme' | 'industry', { items: Source[] }>;
  theme_type: ThemeType;
  sort: string;
}
export function hydrateReview(qc: QueryClient, result: ReviewBootstrap, date: string | null,
  timing: { durationMs: number; traceId?: string; serverTiming?: string; early?: boolean }) {
  const pref = result.classification;
  const buckets = { concept: eviFor(pref.concept), industry: eviFor(pref.industry) };
  qc.setQueryData(['lur', 'classification'], { ...pref, readTiming: { ...timing, operation: 'bootstrap' } });
  qc.setQueryData(['lur', 'days', buckets[result.theme_type]], result.days);
  if (result.day) qc.setQueryData(lurDayQuery(date, result.sort, result.theme_type, buckets).queryKey,
    { ...result.day, readTiming: timing });
  for (const purpose of ['theme', 'industry'] as const) {
    qc.setQueryData(['classifications', 'sources', purpose, 'confidence', true], result.sources[purpose]);
  }
}

interface EarlyResponse {
  data: ReviewBootstrap;
  headers: Record<string, string | undefined>;
  durationMs: number;
  receivedAt: number;
}
declare global {
  interface Window {
    __reviewReadRequested?: boolean;
    __reviewEarlyRead?: { url: string; date: string | null; startedAt: number;
      matchesAuth: () => boolean; promise: Promise<EarlyResponse | null> };
  }
}

export interface PreparedReview {
  date: string | null;
  startedAt: number;
  receivedAt: number;
  state: DehydratedState;
}
let pending: { date: string | null; promise: Promise<PreparedReview | undefined> } | undefined;
/** Start the read at route load, overlapping the lazy page's download. Its
 * dehydrated result is handed to the real mounted Provider, never a singleton. */
export function prepareCurrentReviewRoute(): Promise<PreparedReview | undefined> {
  if (typeof window === 'undefined' || window.location.pathname !== '/limit-up-review') return Promise.resolve(undefined);
  window.__reviewReadRequested = true;
  const date = new URLSearchParams(window.location.search).get('date');
  if (pending?.date === date) return pending.promise;
  const start = performance.now();
  let startedAt = Date.now();
  const candidate = window.__reviewEarlyRead;
  const expectedUrl = new URL((api.defaults.baseURL ?? '/api/v1').replace(/\/$/, '') + '/limit-up-review/bootstrap', window.location.href);
  if (date !== null) expectedUrl.searchParams.set('date', date);
  const early = candidate && candidate.url === expectedUrl.href && candidate.date === date &&
    candidate.matchesAuth() && Date.now() - candidate.startedAt < 5000 ? candidate : undefined;
  if (candidate) delete window.__reviewEarlyRead;
  const promise = (async () => {
    try {
      let preloaded = early ? await early.promise : null;
      if (preloaded && early && !early.matchesAuth()) preloaded = null;
      const response = preloaded ?? await api.get<ReviewBootstrap>('/limit-up-review/bootstrap', { params: { date: date ?? undefined }, timeout: 5000 });
      if (preloaded && early) startedAt = early.startedAt;
      if (window.location.pathname !== '/limit-up-review' || new URLSearchParams(window.location.search).get('date') !== date) return;
      const cache = new QueryClient();
      hydrateReview(cache, response.data, date, { durationMs: preloaded?.durationMs ?? Math.round(performance.now() - start),
        traceId: response.headers?.['x-runtime-trace'], serverTiming: response.headers?.['server-timing'], early: !!preloaded });
      const state = dehydrate(cache);
      cache.clear();
      return { date, startedAt, receivedAt: preloaded?.receivedAt ?? Date.now(), state };
    } catch { /* Existing page reads own errors/retry; no fabricated cache. */ }
  })();
  pending = { date, promise };
  void promise.finally(() => { if (pending?.promise === promise) pending = undefined; });
  return promise;
}

export function withPreparedReview(Page: ComponentType, prepared: PreparedReview | undefined): ComponentType {
  return function ReviewRoute() {
    const client = useQueryClient();
    const version = client.getQueryState(['lur', 'classification'])?.dataUpdatedAt;
    const safe = prepared && typeof window !== 'undefined' &&
      window.location.pathname === '/limit-up-review' &&
      new URLSearchParams(window.location.search).get('date') === prepared.date &&
      Date.now() - prepared.receivedAt < 60_000 &&
      !client.isMutating({ mutationKey: ['lur', 'classification', 'save'] }) &&
      (version === undefined || version <= prepared.startedAt);
    return createElement(HydrationBoundary, { state: safe ? prepared.state : undefined }, createElement(Page));
  };
}
